import React from 'react';
import CategoryTitleAndDescription from '@srcDS/components/molecules/categoryTitleAndDescription';
import styles from '@srcDS/components/templates/category/Category.module.scss';
import ProductNostoTile from '@srcDS/components/organisms/searchDropdown/ProductNostoTile';
import { useNostoPlacementProducts } from '@srcDS/components/organisms/searchDropdown/hooks/useNostoPlacementProducts';
import productStyles from '@srcDS/components/organisms/categoryProductList/CategoryProductList.module.scss';
import localStyles from './SearchNoResults.module.scss';

import { shallowEqual, useSelector } from 'react-redux';
import clsx from 'clsx';
import { Spinner } from '@srcDS/components/atoms/icons';
import { Text } from '@srcDS/components/typography/text/Text';

interface SearchNoResultsProps {
  title: string;
  subtitle?: string;
  searchPhrase: string;
  nostoPlacementId?: string;
  currency: string;
  isSSR: boolean;
}

interface RootState {
  app: {
    context: {
      session: {
        loggedIn: boolean;
        account: {
          projectSpecificData: {
            customerGroupId: string;
          };
        };
      };
    };
  };
}

const SearchNoResults: React.FC<SearchNoResultsProps> = ({
  title,
  subtitle = 'Sorry - Unfortunately there are no products related to your search. Try browsing our bestsellers below',
  searchPhrase,
  nostoPlacementId,
  currency,
  isSSR,
}) => {
  const { loggedIn, customerGroupId } = useSelector(
    (state) => ({
      loggedIn: (state as RootState).app.context.session?.loggedIn,
      customerGroupId: (state as RootState).app.context.session?.account?.projectSpecificData?.customerGroupId ?? null,
    }),
    shallowEqual,
  );

  const { products, isLoading } = useNostoPlacementProducts({
    placement: nostoPlacementId,
    currency,
    isSSR,
    searchPhrase,
  });

  const renderProducts = () => {
    const productProps = {
      loggedIn,
      customerGroupId,
    };

    return products.map((product) => (
      <ProductNostoTile product={product} key={`recommended-product-${product?.productId}`} {...productProps} />
    ));
  };

  if (isLoading) {
    return (
      <div>
        <div className={localStyles.loader}>
          <Spinner />
          <Text as="p">Loading results for ‘{searchPhrase}’</Text>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.pageTitle}>
        <div className={styles.emptySearchTitle}>
          <CategoryTitleAndDescription title={title} subtitle={subtitle} searchPhrase={searchPhrase} />
        </div>
      </div>
      <div className={clsx(productStyles.root, productStyles.layout_wide)}>{renderProducts()}</div>
    </div>
  );
};

export default SearchNoResults;
